import { Button as ButtonJoy, Theme } from '@mui/joy';
import { styled } from '@mui/joy/styles';
import Link from 'next/link';
import { ReactNode, MouseEvent } from 'react';
import styleToCss from 'style-object-to-css-string';
import { UrlObject } from 'url';

export enum ButtonVariants {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

export enum ButtonSizes {
  DEFAULT = 'default',
  SMALL = 'small',
  MEDIUM = 'medium',
}

export interface ButtonProps {
  type?: 'button' | 'submit';
  variant?: ButtonVariants;
  size?: ButtonSizes;
  label?: string | ReactNode;
  href?: string | UrlObject;
  customWidth?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  disabled?: boolean;
  name?: string;
  startDecorator?: ReactNode;
  endDecorator?: ReactNode;
  sx?: Record<string, unknown>;
}

const Button = ({
  type = 'button',
  href,
  variant = ButtonVariants.PRIMARY,
  size = ButtonSizes.DEFAULT,
  label,
  customWidth,
  ...rest
}: ButtonProps): JSX.Element => {
  const commonCss = ({ theme }: { theme: Theme }) => `
    height: ${size === ButtonSizes.DEFAULT ? '48px' : '40px'};
    width: ${customWidth || (size === ButtonSizes.DEFAULT ? '328px' : '65px')};
    text-align: center;
    border-radius: 2px;
    ${styleToCss(theme.typography.label)}
  `;

  const StyledButton =
    variant === ButtonVariants.PRIMARY
      ? styled(ButtonJoy)`
          ${commonCss}
          color: white;
          min-height: 48px;
          background-color: ${({ theme }) => theme.vars.palette.brown};
          &:hover {
            background-color: ${({ theme }) => theme.vars.palette.hoverBrown};
          }
          &:disabled {
            background-color: ${({ theme }) => theme.vars.palette.grey3};
          }
        `
      : styled(ButtonJoy)`
          ${commonCss}
          border: 1px solid ${({ theme }) => theme.vars.palette.brown};
          color: ${({ theme }) => theme.vars.palette.brown};
          background-color: transparent;
          &:hover {
            color: white;
            background-color: ${({ theme }) => theme.vars.palette.hoverBrown};
          }
          &:disabled {
            border: 1px solid ${({ theme }) => theme.vars.palette.grey3};
            color: ${({ theme }) => theme.vars.palette.grey3};
        `;

  const LabeledButton = (
    <StyledButton type={type} {...rest}>
      {label}
    </StyledButton>
  );

  return href ? (
    <Link href={href} passHref>
      {LabeledButton}
    </Link>
  ) : (
    LabeledButton
  );
};

export default Button;
